import { getMerchantPhoneLoginHero } from '@assets/illustrations';
import { getMerchantDineSavvyLogo } from '@assets/logos';
import { merchantPhoneLoginSchema } from '@common/validators';
import env from '@config/env';
import { ROUTE_PATHS } from '@config/routes';
import AuthFormPanel from '@modules/merchant/authentication/components/AuthFormPanel';
import AuthHeroPanel from '@modules/merchant/authentication/components/AuthHeroPanel';
import AuthSplitLayout from '@modules/merchant/authentication/components/AuthSplitLayout';
import PhoneNumberField from '@modules/merchant/authentication/components/PhoneNumberField';
import SmsConsentField from '@modules/merchant/authentication/components/SmsConsentField';
import { sendMerchantOtp } from '@redux/auth/authSlice';
import { Formik, Form } from 'formik';
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

const LoginPage = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pendingPhone = useSelector((state) => state.auth.pendingPhone);
  const heroImage = getMerchantPhoneLoginHero();
  const dineSavvyLogo = getMerchantDineSavvyLogo();

  useEffect(() => {
    document.title = `Login | ${env.APP_NAME}`;
  }, []);

  const handleSubmit = async (values) => {
    try {
      await dispatch(sendMerchantOtp(values.phone)).unwrap();
      navigate(ROUTE_PATHS.VERIFY_OTP);
    } catch {
      // Error toast is handled by the axios interceptor.
    }
  };

  return (
    <AuthSplitLayout>
      <AuthHeroPanel src={heroImage} alt="Fine dining experience" />

      <AuthFormPanel>
        <img
          src={dineSavvyLogo}
          alt="Dine Savvy"
          className="h-12 w-auto shrink-0 md:h-[60px] desktop:h-20"
          width={181}
          height={80}
        />

        <div className="flex w-full flex-1 flex-col justify-center gap-page">
          <div className="flex w-full flex-col gap-0.75 desktop:gap-1">
            <h1 className="desktop:text-[32px] text-[24px] font-semibold leading-tight text-merchant-text-primary">
              Enter your phone number
            </h1>
            <p className="desktop:text-[16px] text-sm font-medium leading-normal text-merchant-text-secondary">
              We&apos;ll send you an OTP to verify your number.
            </p>
          </div>

          <Formik
            initialValues={{
              phone: pendingPhone != null ? String(pendingPhone) : '',
              smsConsent: Boolean(pendingPhone),
            }}
            enableReinitialize
            validationSchema={merchantPhoneLoginSchema}
            validateOnChange
            validateOnMount
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting }) => {
              const canContinue = merchantPhoneLoginSchema.isValidSync(values);

              return (
                <Form className="flex w-full flex-col gap-form">
                  <PhoneNumberField />
                  <SmsConsentField />
                  <button
                    type="submit"
                    disabled={!canContinue || isSubmitting}
                    className="w-full desktop:min-h-11 min-h-8 cursor-pointer rounded-full bg-merchant-action px-(--space-section) py-2.5 desktop:py-3 desktop:text-[16px] text-sm font-semibold leading-3.75 desktop:leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending OTP...' : 'Continue'}
                  </button>
                </Form>
              );
            }}
          </Formik>
        </div>
      </AuthFormPanel>
    </AuthSplitLayout>
  );
});

LoginPage.displayName = 'MerchantLoginPage';

export default LoginPage;
